<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('clientAreaStatisticsPage.pageTitle') }}
    </v-toolbar>

    <v-sheet color="grey lighten-3"
             class="px-4 pt-4 pb-3"
             tile>
      <h3 class="transition-swing font-weight-medium">{{ $t('statisticsTable.filterTitle') }}</h3>
      <v-responsive>
        <v-row>
          <v-col cols="12" sm="6" md="3" class="py-2">
            <v-autocomplete v-model="filters.regionId"
                            :items="$store.getters.appRegions"
                            :label="$t('statisticsTable.labels.regions')"
                            :dense="true"
                            :clearable="true"
                            item-text="name"
                            item-value="id"
                            @change="redirectToPage()"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3" class="py-2">
            <v-autocomplete v-model="filters.client"
                            :items="clients"
                            :label="$t('statisticsTable.labels.organization')"
                            :dense="true"
                            item-text="name"
                            item-value="id"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3" class="py-2">
            <DateInput v-model="filters.start"
                       :label="$t('statisticsTable.labels.startDate')"
                       :dense="true"
                       min-width="0"
                       max-width="80%"
                       :clearable="false"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3" class="py-2">
            <DateInput v-model="filters.end"
                       :label="$t('statisticsTable.labels.endDate')"
                       :dense="true"
                       min-width="0"
                       max-width="80%"
                       :clearable="false"
            />
          </v-col>
          <v-col cols="12" sm="6" md="12" class="py-2 mt-auto mb-auto d-flex justify-space-between">
            <v-btn large color="primary" @click="load">
              {{ $t('statisticsTable.actions.update') }}
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <div>
              <v-btn large color="warning" class="me-2" dark @click="onExcelButtonClicked">{{ $t('statisticsTable.actions.toExcel') }}</v-btn>
              <v-btn large color="primary" @click="onPrintClicked">
                {{ $t('statisticsTable.actions.print') }}
                <v-icon>mdi-printer</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-responsive>
    </v-sheet>

    <v-card-text>
      <v-simple-table :dense="true">
        <template v-slot:default>
          <thead>
          <tr>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.number') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.district') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.acceptedForPeriod') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.over3Sheets') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.pageCount') }}</th>
            <th colspan="2" class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.price') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.sumForRegular') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.sumForOver3Sheets') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.totalSum') }}
            </th>
          </tr>
          <tr>
            <th class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.regularMail') }}
            </th>
            <th class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.over3Sheets') }}
            </th>
          </tr>
          </thead>
          <tbody>
          <template v-if="stats.length">
            <tr v-for="(item, index) in stats" :key="item.area.id">
              <td class="text-center">{{ index + 1 }}</td>

              <td class="text-left">{{ item.area.name }}</td>
              <td class="text-center">{{ item.arrivedCount | decimalNoTrim  }}</td>

              <td class="text-center">{{ item.overPagesSum | decimalNoTrim }}</td>
              <td class="text-center">{{ item.pagesCount | decimalNoTrim }}</td>

              <td class="text-center">{{ item.priceSimple | decimalNoTrim }}</td>
              <td class="text-center">{{ item.pricePerOverPage | decimalNoTrim }}</td>

              <td class="text-center">{{ item.costSimple | decimalNoTrim }}</td>
              <td class="text-center">{{ item.costOverPage | decimalNoTrim }}</td>
              <td class="text-center">{{ item.totalCost | decimalNoTrim }}</td>
            </tr>

            <tr>
              <td class="text-center font-weight-black" colspan="2">{{ $t('statisticsTable.footer.total') }}</td>
              <td class="text-center font-weight-black">{{ totalStats.arrivedCount | decimalNoTrim }}</td>

              <td class="text-center font-weight-black">{{ totalStats.overPagesSum | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.pagesCount | decimalNoTrim }}</td>

              <td class="text-center font-weight-black">{{ totalStats.priceSimple | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.pricePerOverPage | decimalNoTrim }}</td>

              <td class="text-center font-weight-black">{{ totalStats.costSimple | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.costOverPage | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.totalCost | decimalNoTrim }}</td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="11" class="text-center">{{ $t('statisticsTable.noData') }}</td>
            </tr>
          </template>

          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP, LAYOUT_TITLE_CHANGED} from "@/store/modules/layout/actions";
import StatisticsService from "@/services/statistics.service";
import _ from "underscore";
import Mappers from "@/mappers";
import DateInput from "@/components/input/DateInput";
import Moment from "moment";
import ClientService from "@/services/client.service";
import {PRICE_OVER_PAGE, PRICE_SIMPLE} from "@/contants/constants";
import XLSX from "xlsx";


export default {
  name: "ClientAreaStatisticsPage",
  components: {
    DateInput
  },
  data() {
    return {
      organizations: [],
      clients: [],
      stats: [],
      totalStats: {
        arrivedCount: 0,
        overPagesSum: 0,
        pagesCount: 0,
        priceSimple: '',
        pricePerOverPage: '',
        costSimple: 0,
        costOverPage: 0,
        totalCost: 0
      },
      filters: {
        regionId: null,
        client: null,
        start: Moment(),
        end: Moment()
      },
      pfilters: {
        regionId: null,
        client: null,
        start: Moment(),
        end: Moment(),
        isExcel: false
      }
    }
  },
  methods: {
    formatNumberWithSpaces(number) {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    onExcelButtonClicked(){
      this.filters.isExcel = true;
      this.load()
    },
    redirectToPage() {
      if (this.regionId == null) {
        this.$router.push({ name: 'admin-client-statistics' });
      }
      else {
        this.load()
      }
    },
    load() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      this.totalStats.arrivedCount = 0
      this.totalStats.overPagesSum = 0
      this.totalStats.pagesCount = 0

      this.totalStats.costSimple = 0
      this.totalStats.costOverPage = 0
      this.totalStats.totalCost = 0

      StatisticsService.getClientAreaStatistics({
        regionId: this.filters.regionId,
        clientId: this.filters.client,
        start: this.filters.start,
        end: this.filters.end
      })
          .then(response => {
            this.stats = _.map(response.data, s => {
              return {
                area: _.find(this.$store.getters.appAreas, ar => { return ar.id === s.Area.Id }),
                arrivedCount: s.Count,
                overPagesSum: s.OverPagesSum,
                pagesCount: s.PagesCount,
                priceSimple: PRICE_SIMPLE,
                pricePerOverPage: PRICE_OVER_PAGE,
                costSimple: s.Count * PRICE_SIMPLE,
                costOverPage: s.OverPagesSum * PRICE_OVER_PAGE,
                totalCost: (s.OverPagesSum * PRICE_OVER_PAGE) + (s.Count * PRICE_SIMPLE)
              }
            })
            _.each(this.stats, s => {
              this.totalStats.arrivedCount += s.arrivedCount
              this.totalStats.overPagesSum += s.overPagesSum
              this.totalStats.pagesCount += s.pagesCount

              this.totalStats.costSimple += s.costSimple
              this.totalStats.costOverPage += s.costOverPage
              this.totalStats.totalCost += s.totalCost
            })

            if (this.filters.isExcel) {
              const workbook = XLSX.utils.book_new();
              const worksheet = XLSX.utils.aoa_to_sheet([
                [
                  this.$t('statisticsTable.headers.number'),
                  this.$t('statisticsTable.headers.district'),
                  this.$t('statisticsTable.headers.acceptedForPeriod'),
                  this.$t('statisticsTable.headers.over3Sheets'),
                  this.$t('statisticsTable.headers.pageCount'),
                  this.$t('statisticsTable.headers.regularMail'),
                  this.$t('statisticsTable.headers.over3Sheets'), // Price for over 3 sheets
                  this.$t('statisticsTable.headers.sumForRegular'),
                  this.$t('statisticsTable.headers.sumForOver3Sheets'),
                  this.$t('statisticsTable.headers.totalSum')
                ],
              ]);

              worksheet['!cols'] = [{ width: 10 }, { width: 20 }, { width: 15 }, { width: 15 }, { width: 20 }, { width: 15 }, { width: 15 }, { width: 20 }, { width: 25 }, { width: 20 }];
              worksheet['!rows'] = [{ hpx: 25 }];

              for (let i = 1; i <= 10; i++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: i - 1 });
                const cell = worksheet[cellAddress];
                if (!cell) continue;

                cell.s = {
                  alignment: {
                    horizontal: 'center',
                    vertical: 'center',
                  },
                  fill: {
                    fgColor: { rgb: '4472C4' },
                  },
                  border: {
                    top: { style: 'thin', color: { rgb: '000000' } },
                    bottom: { style: 'thin', color: { rgb: '000000' } },
                    left: { style: 'thin', color: { rgb: '000000' } },
                    right: { style: 'thin', color: { rgb: '000000' } },
                  },
                };
              }

              this.stats.forEach((item, index) => {
                const row = [
                  index + 1,
                  item.area ? item.area.name : '',
                  this.formatNumberWithSpaces(item.arrivedCount),
                  this.formatNumberWithSpaces(item.overPagesSum),
                  this.formatNumberWithSpaces(item.pagesCount),
                  this.formatNumberWithSpaces(item.priceSimple),
                  this.formatNumberWithSpaces(item.pricePerOverPage),
                  this.formatNumberWithSpaces(item.costSimple),
                  this.formatNumberWithSpaces(item.costOverPage),
                  this.formatNumberWithSpaces(item.totalCost)
                ];

                XLSX.utils.sheet_add_aoa(worksheet, [row], { origin: index + 1 });
              });

              XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

              const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
              const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'statistics.xlsx');
              document.body.appendChild(link);
              link.click();

              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    loadClients() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      return ClientService.getClientTypes({})
          .then(response => {
            this.clients = _.map(response.data, c => {
              return Mappers.mapClientType(c)
            })

            if (this.clients.length) {
              this.filters.client = this.clients[8].id
            }
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    onPrintClicked() {
      this.loading = true;
      this.$store.dispatch(LAYOUT_LOADING_START)

      StatisticsService.printClientAreaStatistics({
        regionId: this.filters.regionId,
        clientId: this.filters.client,
        start: this.filters.start,
        end: this.filters.end,
        isPdf: true
      })
          .then(() => { })
          .catch(() => { })
          .finally(() => {
            this.loading = false;
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    isClientTypeIsBank(clientId) {
      let isBank = false;
      switch (clientId) {
        case 8: isBank = true; break;
        case 11: isBank = true; break;
        case 12: isBank = true; break;
        case 13: isBank = true; break;
        case 14: isBank = true; break;
        case 15: isBank = true; break;
        case 16: isBank = true; break;
        case 22: isBank = true; break;
        case 23: isBank = true; break;
        case 24: isBank = true; break;
        case 28: isBank = true; break;
      }

      return isBank;
    }
  },
  created() {
    this.$store.dispatch(LAYOUT_TITLE_CHANGED, this.$t('clientAreaStatisticsPage.createdPageTitle'))

    this.filters.regionId = this.$route.params.regionId;
    this.pfilters = this.$route.params.pfilters;

    if (this.pfilters !== null){
      this.filters.inn = this.pfilters.inn
      this.filters.unitId = this.pfilters.unitId
      this.filters.globalUnitId = this.pfilters.globalUnitId
      this.filters.start = this.pfilters.start
      this.filters.end = this.pfilters.end
    }

    if (this.$route.query.inn)
      this.filters.inn = this.$route.query.inn

    this.loadClients()
        .then(() => {
          this.load()
        })
  }
}
</script>

<style scoped>
td, th {
  border: thin solid rgba(0, 0, 0, 0.12);
}

th {
//border: thin solid rgba(39, 22, 22, 68) !important;
}
</style>